/**
 * CORE-1230 — отчёт мастера о прошедшем офлайн-событии.
 *
 * Отчёт — обычная публикация мастера, только с `EventReportForId` (BE
 * CORE-1247). Отдельной формы у него нет: пишется он в том же композере
 * стены, который композер и так умеет, а этот модуль решает три вопроса, на
 * которые композеру отвечать незачем:
 *  - можно ли о событии рассказать прямо сейчас и если нет — почему;
 *  - как понять отказ сервера, чтобы показать причину, а не «проверьте
 *    введённые данные»;
 *  - откуда взять событие по одному номеру.
 *
 * Модуль без React и без React Native намеренно — его правила проверяются
 * тестами, а компоненты в jest этого репозитория не импортируются.
 */
import type { MasterEvent, UnifiedEvent } from '../types/auth';

/** Минимум о событии, нужный решению «можно ли о нём рассказать». */
export interface EventReportSubject {
  id: number;
  masterProfileId: number | null;
  isOnline: boolean;
  startDateUtc: string;
  endDateUtc: string | null;
  title: string;
}

/**
 * Почему рассказать нельзя. Каждое значение — отдельная строка интерфейса:
 * у человека в каждом случае разный следующий шаг.
 *
 *  - `deleted` — события больше нет (мастер удалил). Отчёт не к чему привязать.
 *  - `not_own` — событие чужое. Сервер такой отчёт отвергнет.
 *  - `online` — онлайн-встреча: отчёт — про офлайн (CORE-1246 предлагает его
 *    только офлайн-событиям). Сервер онлайн не отвергает, отсекаем мы — так
 *    же, как не предлагаем.
 *  - `not_ended` — событие ещё впереди или идёт.
 *  - `already_reported` — отчёт уже опубликован: второго сервер не примет
 *    (уникальный индекс), и дубля быть не должно.
 */
export type EventReportBlocker = 'deleted' | 'not_own' | 'online' | 'not_ended' | 'already_reported';

export const EVENT_REPORT_BLOCKERS: readonly EventReportBlocker[] = [
  'deleted', 'not_own', 'online', 'not_ended', 'already_reported',
];

/**
 * Когда событие закончилось: явная дата окончания, иначе начало.
 *
 * Правило не наше — это `MasterEventSchedule.EndsAtUtc` бэкенда: по нему
 * сервер решает, принять ли отчёт, а CORE-1246 — когда предложить его
 * написать. Своё, «более разумное» правило здесь означало бы кнопку, которая
 * получает отказ сервера.
 */
export function eventEndsAt(ev: Pick<EventReportSubject, 'startDateUtc' | 'endDateUtc'>): number {
  return new Date(ev.endDateUtc ?? ev.startDateUtc).getTime();
}

/** Событие уже прошло к моменту `now` — строго позже окончания, как на сервере. */
export function hasEventEnded(ev: Pick<EventReportSubject, 'startDateUtc' | 'endDateUtc'>, now: number): boolean {
  const end = eventEndsAt(ev);
  return Number.isFinite(end) && now > end;
}

export interface EventReportContext {
  /** Мастерский профиль того, кто пишет. */
  myMasterProfileId: number | null | undefined;
  /** id уже опубликованного отчёта, если сервер о нём сказал. */
  reportPostId?: number | null;
  now: number;
}

/**
 * Что мешает рассказать о событии — или `null`, если ничего.
 *
 * Порядок проверок не случаен: о чужом событии не сообщаем, прошло ли оно и
 * есть ли к нему отчёт, — это не дело того, кто спрашивает.
 */
export function eventReportBlocker(ev: EventReportSubject, ctx: EventReportContext): EventReportBlocker | null {
  if (ctx.myMasterProfileId == null || ev.masterProfileId !== ctx.myMasterProfileId) return 'not_own';
  if (ev.isOnline) return 'online';
  if (!hasEventEnded(ev, ctx.now)) return 'not_ended';
  if (typeof ctx.reportPostId === 'number' && ctx.reportPostId > 0) return 'already_reported';
  return null;
}

/**
 * Дословные ответы `CommunityFeedService.CreatePostAsync` (CORE-1247) на
 * запрос с `EventReportForId`. Сервер отвечает 400 и английской фразой без
 * кода, поэтому разбор — по тексту; любой другой текст остаётся обычной
 * ошибкой публикации и не превращается в «отчёт уже есть».
 */
const SERVER_REFUSALS: Readonly<Record<string, EventReportBlocker>> = {
  'Event not found.': 'deleted',
  'You can only report on your own event.': 'not_own',
  'The event has not ended yet.': 'not_ended',
  'This event already has a report.': 'already_reported',
};

/** Отказ сервера на публикацию отчёта — или `null`, если это другая ошибка. */
export function eventReportRefusal(err: unknown): EventReportBlocker | null {
  const e = err as { status?: unknown; message?: unknown; Message?: unknown } | null | undefined;
  if (!e || e.status !== 400) return null;
  const raw = typeof e.message === 'string' ? e.message : typeof e.Message === 'string' ? e.Message : null;
  if (!raw) return null;
  return SERVER_REFUSALS[raw.trim()] ?? null;
}

/** Событие из общего каталога (`GET /api/events/master/{id}`) → субъект отчёта. */
export function subjectFromUnifiedEvent(ev: UnifiedEvent): EventReportSubject {
  return {
    id: ev.id,
    masterProfileId: ev.masterProfileId ?? null,
    isOnline: !!ev.isOnline,
    startDateUtc: ev.startDateUtc,
    endDateUtc: ev.endDateUtc ?? null,
    title: ev.title ?? '',
  };
}

/**
 * `reportPostId` события, если сервер его прислал.
 *
 * Поле отдаёт `GET /api/masterprofile/{id}/events/{eventId}` (CORE-1247), но
 * Swagger его не документирует: ответ этой ручки не типизирован. Поэтому оно
 * необязательное и проверяется на тип — отсутствие поля значит «не знаем», а
 * не «отчёта нет», и последнее слово остаётся за ответом на публикацию.
 */
export function reportPostIdOf(ev: Pick<MasterEvent, 'reportPostId'> | null | undefined): number | null {
  const id = ev?.reportPostId;
  return typeof id === 'number' && Number.isInteger(id) && id > 0 ? id : null;
}

// ─── Цель отчёта по номеру события ───

/**
 * Состояние композера, которому велели писать отчёт о событии `eventId`.
 *
 * `failed` отдельно от `blocked`: сеть упала — это не ответ про событие, и
 * говорить «событие удалено» по обрыву связи нельзя.
 */
export type EventReportTarget =
  | { kind: 'loading'; eventId: number }
  | { kind: 'failed'; eventId: number }
  | { kind: 'ready'; eventId: number; event: EventReportSubject }
  | {
      kind: 'blocked';
      eventId: number;
      reason: EventReportBlocker;
      event: EventReportSubject | null;
      reportPostId: number | null;
    };

export interface EventReportSources {
  getEvent: (source: string, id: number) => Promise<UnifiedEvent>;
  getMasterEvent: (masterProfileId: number, eventId: number) => Promise<MasterEvent>;
}

/**
 * Найти событие и решить, можно ли о нём рассказать.
 *
 * Два запроса, потому что ни один не отвечает на всё сразу: общий каталог
 * различает «удалено» (404) и «чужое» (другой `masterProfileId`), а ручка
 * профиля мастера на оба отвечает одинаковым 404, зато знает `reportPostId`.
 */
export async function resolveEventReportTarget(
  eventId: number,
  myMasterProfileId: number | null | undefined,
  sources: EventReportSources,
  now: number = Date.now(),
): Promise<EventReportTarget> {
  const ownReport = myMasterProfileId != null
    ? sources.getMasterEvent(myMasterProfileId, eventId).then(reportPostIdOf, () => null)
    : Promise.resolve(null);

  let unified: UnifiedEvent;
  try {
    unified = await sources.getEvent('master', eventId);
  } catch (err) {
    const status = (err as { status?: unknown } | null)?.status;
    if (status === 404) {
      return { kind: 'blocked', eventId, reason: 'deleted', event: null, reportPostId: null };
    }
    return { kind: 'failed', eventId };
  }

  const event = subjectFromUnifiedEvent(unified);
  const reportPostId = await ownReport;
  const reason = eventReportBlocker(event, { myMasterProfileId, reportPostId, now });
  return reason
    ? { kind: 'blocked', eventId, reason, event, reportPostId: reason === 'already_reported' ? reportPostId : null }
    : { kind: 'ready', eventId, event };
}

/**
 * CORE-1229 — куда ведёт «рассказать, как прошло»: своя стена, композер в
 * режиме отчёта. Один адрес на все входы — кнопку на странице события,
 * строку центра уведомлений и нажатие на push: форма одна, и путь к ней тоже.
 * В вебе это `/dashboard?initialTab=feed&reportEventId={id}` (public/sw.js
 * собирает тот же адрес сам — модулей он не импортирует).
 */
export function eventReportScreen(eventId: number): { name: 'Dashboard'; params: { initialTab: 'feed'; reportEventId: number } } {
  return { name: 'Dashboard', params: { initialTab: 'feed', reportEventId: eventId } };
}

/** Номер события из параметра маршрута: в вебе он приходит строкой из адреса. */
export function parseReportEventId(raw: unknown): number | null {
  const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : NaN;
  return Number.isInteger(n) && n > 0 ? n : null;
}
